import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, GridLegacy as Grid } from '@mui/material';
import { MewsSetupRequest } from '@/models/payment';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { InputText } from '@/components/shared/InputText';
import {
    SecondaryButtonStyle,
    PrimaryButtonStyle
} from '@/pages/company/common/IntegrationDialog';

interface MewsConnectProps {
    onClose?: () => void;
    onSaveSuccess: any;
}

export const MewsConnect = (props: MewsConnectProps) => {
    const { t } = useTranslation();
    const [mewsData, setMewsData] = useState<MewsSetupRequest>({ apiKey: '' });

    const { setupMews } = usePaymentsMutations();

    // The key is checked against Mews before it is saved, so the backend's message is the useful
    // one: it separates "Mews rejected this key" from "Mews is not answering, nothing was saved".
    // Passing the error through backendErrorHandler surfaces that detail instead of a generic
    // "connection failed".
    useHandleResponse({
        error: setupMews.error ?? null,
        isSuccess: setupMews.isSuccess,
        isPending: setupMews.isPending,
        successAlertMessage: t('mews.connection_verified'),
        isSuccessCallBack: () => props.onSaveSuccess?.()
    });

    const isFormValid = () => mewsData.apiKey.trim() !== '';

    const onClick = () => {
        setupMews.mutate({ apiKey: mewsData.apiKey.trim() });
    };

    return (
        <>
            <Box width={'100%'} margin={'20px 0 10px'}>
                <InputText
                    // Write-only: the stored key is never sent back to the browser, so this field
                    // starts empty even for a company that is already connected.
                    type="password"
                    autoComplete="off"
                    value={mewsData.apiKey}
                    label={t('mews.api_key')}
                    placeholder={t('mews.enter_api_key')}
                    onChange={(e) => {
                        setMewsData({ ...mewsData, apiKey: e.target.value });
                    }}
                    spacing="10px 0"
                />
            </Box>

            <Grid m={'20px 0'} container justifyContent={'space-around'}>
                <SecondaryButtonStyle onClick={props.onClose}>
                    {t('back')}
                </SecondaryButtonStyle>
                <PrimaryButtonStyle
                    disabled={!isFormValid() || setupMews.isPending}
                    onClick={onClick}
                >
                    {t('connect')}
                </PrimaryButtonStyle>
            </Grid>
        </>
    );
};
